import { useMemo } from "react";
import { AlertTriangle } from "lucide-react";
import type { ViewPrimitive, BladeContext } from "../../shell/registry";
import {
  BladeLoading,
  BladeError,
  BladeDenied,
  BladeEmpty,
  forcedBladeState,
} from "../../shell/BladeStates";
import { useOrgDashboard } from "../AuditDataSource";
import { reasonLabel } from "../data/reasons";
import type { CodeCaseQuery, OrgDashboard } from "../types";
import { fieldLabel } from "./codeTableToolbar";
import { AGENT_COLOR, CODER_COLOR, ErroredCodeTable, hasSideSplit } from "./erroredCodeTable";
import { CorpusCodeTable } from "./corpusCodeTable";
import { FieldReasonsSection, ReasonList } from "./reasonLists";
import { agentRate, agreementRate, coderRate, weeklyDelta, type RateDelta } from "./metricsTrend";
// The segmented-bar vocabulary (keys, labels, colours) is shared with the
// stacked trend bars so the two read as one measure — see `segments.ts`.
import { CORPUS_SEGMENTS, OUTCOME_SEGMENTS } from "./segments";
// Explicit extension: this directory carries both a pure `metricsTrend.ts`
// (the functions above) and a `metricsTrend.tsx` (the components below) with
// the same base name — the bare specifier resolves to the .ts file, so the
// component exports need the extension spelled out (allowImportingTsExtensions).
import { DeltaLine, TrendSection } from "./metricsTrend.tsx";

// Program-metrics view-primitive: how the coding agent is doing against the
// human coder, as judged by reviewers.
//
// This is the ONLY audit surface that speaks in provenance, and it is safe
// because everything here is an aggregate — no case id, no reviewer, nothing
// that lets a reader map "the agent" back onto slot A or B of a case they are
// about to review. Per-case provenance never reaches the client at all; the
// server unblinds at fold time and this screen consumes the fold.

/** Rates arrive in [0,1], or -1 meaning NOT MEASURED. */
const NOT_MEASURED = -1;

function isMeasured(rate: number): boolean {
  return rate !== NOT_MEASURED && Number.isFinite(rate);
}

function pct(rate: number): string {
  return `${(rate * 100).toFixed(1)}%`;
}

function RateCard({
  eyebrow,
  rate,
  hint,
  emphasis,
  delta,
}: {
  eyebrow: string;
  rate: number;
  hint: string;
  emphasis?: boolean;
  delta?: RateDelta | null;
}) {
  const measured = isMeasured(rate);
  return (
    <div
      className={`flex flex-col gap-1 rounded-coronis border bg-card px-4 py-3.5 ${
        emphasis ? "border-coronis-blue-200" : "border-border"
      }`}
    >
      <span className="text-[11px] font-medium uppercase tracking-[0.08em] text-muted-foreground">
        {eyebrow}
      </span>
      {measured ? (
        <span className="text-[28px] font-semibold leading-none tabular-nums text-foreground">
          {pct(rate)}
        </span>
      ) : (
        // Never render an unmeasured rate as 0% — on an unstarted study that
        // reads as a model that got everything wrong.
        <span className="text-[19px] font-medium leading-tight text-muted-foreground">
          Not measured yet
        </span>
      )}
      <span className="text-[11.5px] leading-snug text-muted-foreground">
        {hint}
      </span>
      <DeltaLine delta={delta ?? null} />
    </div>
  );
}

/** Stacked outcome bar: both / agent only / coder only / neither.
 *
 *  Widths come from the counts, not from the rates, so a field with two
 *  judgements is visibly not a field with two hundred — the row's `judged`
 *  count sits beside it for exactly that reason. */
function OutcomeBar({
  both,
  agentOnly,
  coderOnly,
  neither,
  judged,
}: {
  both: number;
  agentOnly: number;
  coderOnly: number;
  neither: number;
  judged: number;
}) {
  if (judged <= 0)
    return (
      <span className="text-[11px] text-muted-foreground">No judgements</span>
    );
  const w = (n: number) => `${(n / judged) * 100}%`;
  const segments = OUTCOME_SEGMENTS.map((seg) => ({
    ...seg,
    n: seg.pick({ both, agentOnly, coderOnly, neither }),
  }));
  return (
    <span
      className="flex h-2 w-full overflow-hidden rounded-full bg-muted"
      role="img"
      aria-label={segments
        .filter((s) => s.n > 0)
        .map((s) => `${s.label}: ${s.n}`)
        .join(", ")}
    >
      {segments.map((s) =>
        s.n > 0 ? (
          <span
            key={s.key}
            style={{ width: w(s.n), backgroundColor: s.color }}
            title={`${s.label}: ${s.n}`}
          />
        ) : null,
      )}
    </span>
  );
}

function LegendKey({ color, label }: { color: string; label: string }) {
  return (
    <span className="flex items-center gap-1.5 text-[11.5px] text-muted-foreground">
      <span
        className="size-2 rounded-full"
        style={{ backgroundColor: color }}
        aria-hidden
      />{" "}
      {label}
    </span>
  );
}

function FieldTable({ fields }: { fields: OrgDashboard["fields"] }) {
  // Worst agreement first: the reason to open this screen is to find where the
  // agent and the coder disagree, and alphabetical order buries that.
  const rows = useMemo(
    () =>
      [...fields].sort((a, b) => {
        const agree = (f: OrgDashboard["fields"][number]) =>
          f.judged > 0 ? (f.both + f.neither) / f.judged : 1;
        return agree(a) - agree(b);
      }),
    [fields],
  );

  return (
    <div className="overflow-auto rounded-coronis border border-border">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-border bg-muted/40 text-left text-[11px] font-medium uppercase tracking-[0.04em] text-muted-foreground">
            <th className="py-2 pl-4 pr-3 font-medium">Field</th>
            <th className="px-3 py-2 text-right font-medium">Judged</th>
            <th className="min-w-[180px] px-3 py-2 font-medium">Outcome mix</th>
            <th className="px-3 py-2 text-right font-medium">Agent</th>
            <th className="px-3 py-2 text-right font-medium">Coder</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((f) => {
            const agent =
              f.judged > 0
                ? (f.agentCorrect + f.both) / f.judged
                : NOT_MEASURED;
            const coder =
              f.judged > 0
                ? (f.coderCorrect + f.both) / f.judged
                : NOT_MEASURED;
            return (
              <tr
                key={f.field}
                className="border-b border-border last:border-0"
              >
                <td className="py-2.5 pl-4 pr-3 text-foreground">
                  {fieldLabel(f.field)}
                </td>
                <td className="px-3 py-2.5 text-right tabular-nums text-muted-foreground">
                  {f.judged}
                </td>
                <td className="px-3 py-2.5">
                  <OutcomeBar
                    both={f.both}
                    agentOnly={f.agentCorrect}
                    coderOnly={f.coderCorrect}
                    neither={f.neither}
                    judged={f.judged}
                  />
                </td>
                <td className="px-3 py-2.5 text-right font-semibold tabular-nums text-foreground">
                  {isMeasured(agent) ? pct(agent) : "—"}
                </td>
                <td className="px-3 py-2.5 text-right font-semibold tabular-nums text-foreground">
                  {isMeasured(coder) ? pct(coder) : "—"}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function AgreementBar({
  row,
}: {
  row: OrgDashboard["corpus"]["fields"][number];
}) {
  const total = row.agree + row.differ + row.oneSided + row.notComparable;
  if (total <= 0)
    return <span className="text-[11px] text-muted-foreground">No cases</span>;
  const segments = CORPUS_SEGMENTS.map((seg) => ({ ...seg, n: seg.pick(row) }));
  return (
    <span
      className="flex h-2 w-full overflow-hidden rounded-full bg-muted"
      role="img"
      aria-label={segments
        .filter((s) => s.n > 0)
        .map((s) => `${s.label}: ${s.n}`)
        .join(", ")}
    >
      {segments.map((s) =>
        s.n > 0 ? (
          <span
            key={s.key}
            style={{
              width: `${(s.n / total) * 100}%`,
              backgroundColor: s.color,
            }}
            title={`${s.label}: ${s.n}`}
          />
        ) : null,
      )}
    </span>
  );
}

/** A raw count with its own denominator spelled out.
 *
 *  Separate from RateCard because these are counts, not rates: RateCard's whole
 *  contract is "-1 means not measured", and a count of zero here is a real
 *  measurement — an empty corpus, not an unmeasured one. */
function CountTile({
  eyebrow,
  value,
  hint,
  emphasis,
}: {
  eyebrow: string;
  value: string;
  hint: string;
  emphasis?: boolean;
}) {
  return (
    <div
      className={`flex flex-col gap-1 rounded-coronis border bg-card px-4 py-3.5 ${
        emphasis ? "border-coronis-blue-200" : "border-border"
      }`}
    >
      <span className="text-[11px] font-medium uppercase tracking-[0.08em] text-muted-foreground">
        {eyebrow}
      </span>
      <span className="text-[28px] font-semibold leading-none tabular-nums text-foreground">
        {value}
      </span>
      <span className="text-[11.5px] leading-snug text-muted-foreground">
        {hint}
      </span>
    </div>
  );
}

/** Per-field agreement across the comparable corpus.
 *
 *  The agreement column is quoted over agree+differ, NOT over every case: a
 *  field that could not be compared is not evidence either way, and dividing by
 *  it would report the modifiers row — mostly unscorable by design — as the
 *  worst-agreeing field on the screen. */
function CorpusFieldTable({
  fields,
}: {
  fields: OrgDashboard["corpus"]["fields"];
}) {
  // Worst agreement first, and unscorable fields last rather than first: a row
  // with nothing to compare is not the worst-agreeing row, it is no row at all.
  const rows = useMemo(
    () =>
      [...fields].sort((a, b) => {
        const scored = (f: OrgDashboard["corpus"]["fields"][number]) =>
          f.agree + f.differ;
        if (scored(a) === 0 || scored(b) === 0) return scored(b) - scored(a);
        return a.agree / scored(a) - b.agree / scored(b);
      }),
    [fields],
  );
  return (
    <div className="overflow-auto rounded-coronis border border-border">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-border bg-muted/40 text-left text-[11px] font-medium uppercase tracking-[0.04em] text-muted-foreground">
            <th className="py-2 pl-4 pr-3 font-medium">Field</th>
            <th className="min-w-[140px] px-3 py-2 font-medium">Split</th>
            <th className="px-3 py-2 text-right font-medium">Agree</th>
            <th className="px-3 py-2 text-right font-medium">Differ</th>
            <th className="px-3 py-2 text-right font-medium" title="One side produced codes and the other produced none — a coverage gap, not a coding disagreement. Excluded from the agreement rate.">
              One side
            </th>
            <th className="px-3 py-2 text-right font-medium">Not comp.</th>
            <th className="px-3 py-2 pr-4 text-right font-medium">Agreement</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((f) => {
            const scored = f.agree + f.differ;
            return (
              <tr
                key={f.field}
                className="border-b border-border last:border-0"
              >
                <td className="py-2.5 pl-4 pr-3 font-medium text-foreground">
                  {fieldLabel(f.field)}
                </td>
                <td className="px-3 py-2.5">
                  <AgreementBar row={f} />
                </td>
                <td className="px-3 py-2.5 text-right tabular-nums text-foreground">
                  {f.agree.toLocaleString()}
                </td>
                <td className="px-3 py-2.5 text-right tabular-nums text-foreground">
                  {f.differ.toLocaleString()}
                </td>
                <td className="px-3 py-2.5 text-right tabular-nums text-muted-foreground">
                  {f.oneSided.toLocaleString()}
                </td>
                <td className="px-3 py-2.5 text-right tabular-nums text-muted-foreground">
                  {f.notComparable.toLocaleString()}
                </td>
                <td className="px-3 py-2.5 pr-4 text-right font-semibold tabular-nums text-foreground">
                  {scored > 0 ? (
                    pct(f.agree / scored)
                  ) : (
                    // Nothing was scorable. Rendering 0% here would say the two
                    // sides never once agreed on this field, which is the
                    // opposite of "this field could not be compared".
                    <span className="font-normal text-muted-foreground">
                      Not comparable
                    </span>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

/** Agreement measured between the two coded results, with no reviewer input.
 *
 *  Rendered above the verdict-based sections because it is the part of this
 *  screen that says anything before the first review is submitted — and because
 *  it answers a question worth asking first: whether the corpus reviewers are
 *  about to spend time on is sound. */
function CorpusSection({
  corpus,
  onOpenCase,
  onOpenCodeCases,
}: {
  corpus: OrgDashboard["corpus"];
  onOpenCase?: (dcn: string) => void;
  onOpenCodeCases?: (q: CodeCaseQuery) => void;
}) {
  const excluded = corpus.cases - corpus.comparable;
  return (
    <div className="flex flex-col gap-2.5">
      <div className="flex flex-wrap items-baseline justify-between gap-3">
        <h2 className="text-[13px] font-semibold uppercase tracking-[0.08em] text-muted-foreground">
          Corpus agreement
        </h2>
        <span className="flex flex-wrap items-center gap-3">
          {CORPUS_SEGMENTS.map((s) => (
            <LegendKey key={s.key} color={s.color} label={s.label} />
          ))}
        </span>
      </div>
      <p className="text-[12.5px] leading-snug text-muted-foreground">
        Measured between the two coded results directly — no reviewer input, so
        these numbers are available before anyone reviews. They say nothing
        about which side is <em>right</em>: two sides agreeing on a wrong code
        agree. Codes are counted after normalisation, so an FA suffix and an
        ICD-10 vintage difference are the same code, not a disagreement.
      </p>

      <div className="grid grid-cols-[repeat(auto-fit,minmax(190px,1fr))] gap-3">
        <CountTile
          eyebrow="Cases"
          value={corpus.cases.toLocaleString()}
          hint="Every case in the store, comparable or not."
        />
        <CountTile
          eyebrow="Comparable"
          value={corpus.comparable.toLocaleString()}
          hint="Both sides coded and not withheld — the denominator below."
          emphasis
        />
        <CountTile
          eyebrow="Full agreement"
          value={
            corpus.comparable > 0
              ? `${pct(corpus.fullyAgreed / corpus.comparable)}`
              : "—"
          }
          hint={`${corpus.fullyAgreed.toLocaleString()} of ${corpus.comparable.toLocaleString()} comparable cases with no field differing.`}
        />
      </div>

      {corpus.exclusions.length > 0 && (
        <div className="flex flex-col gap-1.5 rounded-coronis border border-border bg-card px-4 py-3">
          <span className="text-[11px] font-medium uppercase tracking-[0.08em] text-muted-foreground">
            {excluded.toLocaleString()}{" "}
            {excluded === 1 ? "case is" : "cases are"} not comparable
          </span>
          {/* Broken out by reason rather than summed: the remedies differ, and
              a single "excluded" number sends a reader looking for one cause. */}
          <ul className="flex flex-col gap-1">
            {corpus.exclusions.map((e) => (
              <li
                key={e.reason}
                className="flex items-baseline justify-between gap-4 text-[13px] text-foreground"
              >
                <span>{e.reason}</span>
                <span className="shrink-0 tabular-nums text-muted-foreground">
                  {e.cases.toLocaleString()}
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {corpus.fields.length > 0 && <CorpusFieldTable fields={corpus.fields} />}

      {corpus.codes.length > 0 && (
        <div className="flex flex-col gap-2.5 pt-1">
          <h3 className="text-[12px] font-semibold uppercase tracking-[0.08em] text-muted-foreground">
            By code
          </h3>
          <CorpusCodeTable codes={corpus.codes} onOpenCase={onOpenCase} onOpenCodeCases={onOpenCodeCases} />
          {corpus.codesSuppressed > 0 && (
            // The table is a top-N. Saying so is the difference between "these
            // are the codes the study has seen" and "these are the most common
            // ones" — a reader who takes the first reading concludes the tail
            // does not exist.
            <p className="text-[11.5px] leading-snug text-muted-foreground">
              {corpus.codesSuppressed.toLocaleString()} further{" "}
              {corpus.codesSuppressed === 1 ? "code is" : "codes are"} not
              listed — this is the most-seen{" "}
              {corpus.codes.length.toLocaleString()}, not the full set.
            </p>
          )}
        </div>
      )}
    </div>
  );
}

function AuditDashboardBody({ ctx }: { ctx: BladeContext }) {
  const { status, data } = useOrgDashboard();

  // Opens the case as a CHILD of this blade, so the breadcrumb records that the
  // reader arrived from the metrics screen rather than from a queue. Matches
  // reviewDescriptor in auditCompare — same key and width, or the compare screen
  // renders in a blade it was not built for.
  const openCase = (dcn: string) => {
    ctx.openChild({
      key: "audit-review",
      entityId: dcn,
      title: dcn,
      width: "xwide",
    });
    ctx.setMode("focus");
  };

  if (status === "loading") return <BladeLoading />;
  if (status === "error")
    return (
      <BladeError message="Something went wrong loading program metrics." />
    );
  if (status === "denied")
    return (
      <BladeDenied description="You don’t have access to program metrics." />
    );
  if (!data)
    return (
      <BladeError message="Something went wrong loading program metrics." />
    );

  // Opens the drill-in blade for one report row (a code, or a reason's code
  // chip). Matches Task 8's "audit-code-cases" primitive — same key, and the
  // same params shape it reads.
  const openCodeCases = (q: CodeCaseQuery) => {
    const what = q.code ? `${q.code} · ${fieldLabel(q.field ?? "")}` : reasonLabel(q.reason ?? "");
    ctx.openChild({
      key: "audit-code-cases",
      title: what,
      eyebrow: q.mode === "corpus" ? "Corpus cases" : "Errored cases",
      width: "xwide",
      params: { mode: q.mode, field: q.field ?? "", code: q.code ?? "", reason: q.reason ?? "" },
    });
    ctx.setMode("focus");
  };
  // The server sends example ids to auditors only, so their presence is the
  // one signal the client has that a drill-in will not be refused.
  const canDrill =
    data.erroredCodes.some((c) => c.exampleDcns.length > 0) ||
    data.corpus.codes.some((c) => c.exampleDcns.length > 0);

  // Week-over-week deltas for the three accuracy cards, from the last two
  // MEASURED weekly buckets. null (rendered as absent, never "0") until two
  // measured weeks exist.
  const deltaAgent = weeklyDelta(data.trend.weekly, agentRate);
  const deltaCoder = weeklyDelta(data.trend.weekly, coderRate);
  const deltaAgreement = weeklyDelta(data.trend.weekly, agreementRate);

  const measured = isMeasured(data.agentAccuracy);
  // HUB-812: the study-wide totals share the trend buckets' blind-review
  // floor. `measured` alone cannot tell "nothing reviewed yet" from "reviewed,
  // but withheld below the floor" apart — both send agentAccuracy = -1 — so
  // this also needs `reviewed > 0`, the signal that there is a real count to
  // explain.
  const withheld = !measured && data.reviewed > 0 && !data.verdictFloor.cleared;
  // The empty gate has to account for the corpus block too. It was
  // reviewer-verdict only, so a study with thousands of coded cases and no
  // reviews yet rendered "Nothing measured yet" over a screen full of numbers
  // it already had — which is precisely the phase this report is for.
  if (!measured && data.excluded === 0 && data.corpus.cases === 0) {
    return (
      <BladeEmpty
        title="Nothing measured yet"
        description="Program metrics appear once cases have been coded by both sides, and accuracy once reviewers submit verdicts."
      />
    );
  }

  return (
    <div className="flex flex-col gap-5">
      <p className="text-sm text-muted-foreground">
        {withheld ? (
          <>
            {data.reviewed.toLocaleString()}{" "}
            {data.reviewed === 1 ? "case" : "cases"} reviewed · verdicts
            withheld until the floor is met
          </>
        ) : (
          <>
            {data.reviewed.toLocaleString()}{" "}
            {data.reviewed === 1 ? "case" : "cases"} reviewed ·{" "}
            {data.judged.toLocaleString()} field{" "}
            {data.judged === 1 ? "judgement" : "judgements"} · rates are over
            judgements, not cases
          </>
        )}
      </p>

      {data.corpus.cases > 0 && (
        <CorpusSection corpus={data.corpus} onOpenCase={openCase} onOpenCodeCases={openCodeCases} />
      )}

      {data.excluded > 0 && (
        // The denominator moved. Saying so is the whole point of carrying
        // `excluded` this far — a rate quoted over a corpus the reader believes
        // is complete is a wrong number, not a rounded one.
        <div
          className="flex items-start gap-2.5 rounded-coronis border px-4 py-3"
          style={{
            backgroundColor: "var(--pill-amber-bg)",
            borderColor: "var(--pill-amber-border)",
          }}
        >
          <AlertTriangle
            className="mt-0.5 size-4 shrink-0"
            style={{ color: "var(--pill-amber-fg)" }}
          />
          <p className="text-[12.5px] leading-snug text-foreground">
            <span className="font-semibold">
              {data.excluded.toLocaleString()}{" "}
              {data.excluded === 1 ? "review is" : "reviews are"} excluded.
            </span>{" "}
            They were submitted under a superseded study key, so the side each
            verdict referred to can no longer be resolved. The rates below are
            over the remaining {data.judged.toLocaleString()} judgements.
          </p>
        </div>
      )}

      <h2 className="text-[13px] font-semibold uppercase tracking-[0.08em] text-muted-foreground">
        Reviewer verdicts
      </h2>
      {/* Explicitly separated from the corpus block above, because the two
          answer different questions over different denominators. Everything
          below needs a human to have judged the case; nothing above does. */}
      {!measured && (
        <p className="text-[12.5px] leading-snug text-muted-foreground">
          {withheld ? (
            <>
              {data.reviewed.toLocaleString()}{" "}
              {data.reviewed === 1 ? "case" : "cases"} reviewed so far.
              Verdict metrics appear once at least{" "}
              {data.verdictFloor.minReviews} reviewed cases from{" "}
              {data.verdictFloor.minReviewers} different reviewers exist —
              below that, a single reviewer could read their own verdict back
              in agent-versus-coder terms.
            </>
          ) : (
            <>
              No verdict has been submitted yet, so nothing below is
              measured. The corpus numbers above do not depend on reviewers
              and are already complete.
            </>
          )}
        </p>
      )}

      <div
        data-tour="tour-period"
        className="grid grid-cols-[repeat(auto-fit,minmax(190px,1fr))] gap-3"
      >
        <RateCard
          eyebrow="Agent accuracy"
          rate={data.agentAccuracy}
          hint="Share of judgements where the reviewer accepted the agent's result."
          emphasis
          delta={deltaAgent}
        />
        <RateCard
          eyebrow="Coder accuracy"
          rate={data.coderAccuracy}
          hint="The same measure for the human baseline."
          delta={deltaCoder}
        />
        <RateCard
          eyebrow="Agreement"
          rate={data.agreement}
          hint="Share the reviewer could not separate — both acceptable, or neither."
          delta={deltaAgreement}
        />
      </div>

      <div className="flex flex-col gap-2.5">
        <h2 className="text-[13px] font-semibold uppercase tracking-[0.08em] text-muted-foreground">
          Against the auditor’s selection
        </h2>
        <div className="grid grid-cols-[repeat(auto-fit,minmax(190px,1fr))] gap-3">
          <RateCard
            eyebrow="Agent vs auditor"
            rate={data.agentSelectionMatch}
            hint="Of the judgements where the auditor named a correct code, the share where it was the agent's."
            emphasis
          />
          <RateCard
            eyebrow="Coder vs auditor"
            rate={data.coderSelectionMatch}
            hint="The same measure for the human baseline."
          />
        </div>
        <p className="text-[12.5px] leading-snug text-muted-foreground">
          A different question from accuracy above, and the difference is the
          denominator: accuracy divides by every judgement, so a case the record
          could not support counts against the agent as hard as one the coder
          won.{" "}
          {data.selectionResolved > 0 ? (
            <>
              This divides by the{" "}
              <span className="font-semibold text-foreground">
                {data.selectionResolved.toLocaleString()}
              </span>{" "}
              {data.selectionResolved === 1 ? "judgement" : "judgements"} where
              the auditor named an answer.
            </>
          ) : (
            // Nothing to divide by. Said in words rather than shown as 0% —
            // "the agent matched the auditor 0% of the time" is a result, and
            // this is the absence of one.
            <>
              No judgement here has an answer to match against, so there is
              nothing to divide by.
            </>
          )}
          {data.selectionUnresolved > 0 && (
            // These rates are an upper bound while this is non-zero, and saying
            // so is the point of carrying the count. A reader who quotes them as
            // final is quoting a number computed over the subset of cases that
            // had an answer at all.
            //
            // The wording no longer says replacements go uncaptured. They do get
            // captured — a rejection where the auditor named the correct code
            // resolves and counts against both sides — so what is left here is
            // specifically the rejections with no answer recorded. Telling a
            // reader the pipeline drops all of them would send them to fix
            // something that is already fixed.
            <>
              {" "}
              <span className="font-semibold text-foreground">
                {data.selectionUnresolved.toLocaleString()}
              </span>{" "}
              {data.selectionUnresolved === 1 ? "judgement" : "judgements"}{" "}
              rejected both sides without recording what should have been coded,
              so nothing can be matched against{" "}
              {data.selectionUnresolved === 1 ? "it" : "them"} — these rates are
              an upper bound while that is true.
            </>
          )}
        </p>
      </div>

      <TrendSection trend={data.trend} />

      {isMeasured(data.agreement) && data.judged > 0 && (
        <p className="text-[12.5px] leading-snug text-muted-foreground">
          Agreement measures indistinguishability, not quality. Of the{" "}
          {(data.both + data.neither).toLocaleString()} judgements where the
          reviewer could not separate the two,{" "}
          <span className="font-semibold text-foreground">
            {data.neither.toLocaleString()}
          </span>{" "}
          rejected both — a high agreement driven by those is a failing
          workflow, not a matched pair.
        </p>
      )}

      <div data-tour="tour-breakdown" className="flex flex-col gap-2.5">
        <div className="flex flex-wrap items-baseline justify-between gap-3">
          <h2 className="text-[13px] font-semibold uppercase tracking-[0.08em] text-muted-foreground">
            By field
          </h2>
          <span className="flex flex-wrap items-center gap-3">
            {OUTCOME_SEGMENTS.map((s) => (
              <LegendKey key={s.key} color={s.color} label={s.label} />
            ))}
          </span>
        </div>
        {data.fields.length > 0 ? (
          <FieldTable fields={data.fields} />
        ) : (
          <p className="text-sm text-muted-foreground">
            No per-field judgements yet.
          </p>
        )}
      </div>

      {/* Its own section, directly under the per-field outcomes it explains:
          the table above says WHICH field goes wrong, this says why. */}
      <FieldReasonsSection fields={data.fields} canDrill={canDrill} onOpenCodeCases={openCodeCases} />

      <div className="flex flex-col gap-2.5">
        <div className="flex flex-wrap items-baseline justify-between gap-3">
          <h2 className="text-[13px] font-semibold uppercase tracking-[0.08em] text-muted-foreground">
            Most-errored codes
          </h2>
          {/* The legend explains the Agent/Coder columns, and those render only
              when the server sent a split — an auditor-only field. A legend for
              columns that are not there reads as a rendering bug. */}
          {hasSideSplit(data.erroredCodes) && (
            <span className="flex flex-wrap items-center gap-3">
              <LegendKey color={AGENT_COLOR} label="Agent" />
              <LegendKey color={CODER_COLOR} label="Coder" />
            </span>
          )}
        </div>
        {data.erroredCodes.length > 0 ? (
          <ErroredCodeTable codes={data.erroredCodes} onOpenCase={openCase} onOpenCodeCases={openCodeCases} />
        ) : (
          <p className="text-sm text-muted-foreground">
            No code has been judged wrong often enough to rank yet.
          </p>
        )}
        {data.erroredCodesSuppressed > 0 && (
          // Say that the list is floored. A truncated table presented as
          // complete is the same class of error as a moved denominator
          // presented as whole — a reader would conclude those codes are clean.
          <p className="text-[11.5px] leading-snug text-muted-foreground">
            {data.erroredCodesSuppressed.toLocaleString()} further{" "}
            {data.erroredCodesSuppressed === 1 ? "code" : "codes"} errored fewer
            than three times and{" "}
            {data.erroredCodesSuppressed === 1 ? "is" : "are"} not listed. A
            code seen erring once is one case, not a pattern — and naming it
            here could tie a result back to a case a reviewer has open.
          </p>
        )}
        {data.erroredCodesCapped > 0 && (
          // A different reason to be missing than the suppressed-floor notice
          // above: these codes cleared the floor but fell past the server's
          // cap, so the table reads as a top-N rather than the full floored set.
          <p className="text-[11.5px] leading-snug text-muted-foreground">
            {data.erroredCodesCapped.toLocaleString()} further{" "}
            {data.erroredCodesCapped === 1 ? "code" : "codes"} cleared the floor
            but {data.erroredCodesCapped === 1 ? "is" : "are"} past the{" "}
            {data.erroredCodes.length.toLocaleString()}-row cap — this is the
            worst {data.erroredCodes.length.toLocaleString()}, not the full set.
          </p>
        )}
      </div>

      {data.topReasons.length > 0 && (
        <div className="flex flex-col gap-2.5">
          <h2 className="text-[13px] font-semibold uppercase tracking-[0.08em] text-muted-foreground">
            Most-cited reasons
          </h2>
          <ReasonList reasons={data.topReasons} canDrill={canDrill} onOpenCodeCases={openCodeCases} />
        </div>
      )}
    </div>
  );
}

export const auditDashboardPrimitive: ViewPrimitive = {
  key: "audit-metrics",
  // Restored when a deep link or refresh decodes this blade from the URL,
  // which carries identity only.
  defaultTitle: "Program metrics",
  defaultEyebrow: "Code evaluation",
  defaultWidth: "xwide",
  render: (ctx) => {
    const forced = forcedBladeState(ctx.descriptor);
    if (forced === "loading") return <BladeLoading />;
    if (forced === "error") return <BladeError />;
    if (forced === "denied") return <BladeDenied />;
    if (forced === "empty") {
      return (
        <BladeEmpty
          title="Nothing measured yet"
          description="Program metrics appear once reviewers have submitted verdicts on coded cases."
        />
      );
    }
    return <AuditDashboardBody ctx={ctx} />;
  },
};
